/**
 * Shared formatting utilities for assets in diffs and documentation.
 * This is the single source of truth for asset formatting to prevent drift
 * between diff output and schema documentation.
 */

import type { AssetDescription } from '../types';
import type { TextColorDescription } from './textColorMap';
import { stringifyUnknown } from 'js/src/libs/custom-utils';

/**
 * The sibling map carrying each marked property's scene-design default. Not a property of the
 * asset in its own right: `formatAsset` folds it into the lines it annotates, and the diff skips
 * it entirely.
 */
export const CHANGED_FROM_DESIGN_KEY = 'changedFromDesign';

/**
 * Format a millisecond duration for display, rounding to the nearest millisecond
 * to avoid noisy floating-point values like "52833.54166666667ms".
 */
export function formatDurationMs(durationMs: number): string {
    return `${Math.round(durationMs)}ms`;
}

/**
 * Helper to format property values for display.
 *
 * Values are rendered in full — never truncated. The agent can only see (and, for tools that must
 * restate a whole value rather than patch it, act on) content that reaches it through this
 * serialized state, so a clipped tail is content it can neither read nor safely reproduce. Guarding
 * against a pathologically large state is done where the state is injected (see
 * isContextMessageOversized), rather than by silently dropping the tail of individual fields here.
 */
function formatPropertyValue(value: unknown): string {
    if (typeof value === 'string') {
        return `"${value}"`;
    }
    return JSON.stringify(value);
}

/** A colour legend entry reads as a role-labelled colour map, not a JSON array. */
export function isColorLegend(value: unknown): value is TextColorDescription[] {
    return (
        Array.isArray(value) &&
        value.every(
            (entry) =>
                entry != null &&
                typeof entry === 'object' &&
                'handle' in entry &&
                'role' in entry &&
                'value' in entry,
        )
    );
}

/**
 * Render a colour map (a text/caption/group asset's `colors`) as a handle-keyed block instead of a
 * JSON array — one line per handle, leading with the meaning:
 *
 *   colors:
 *     text: "#0b1425 [slot 3]"
 *     box_1: "#12343b" (backdrop, alpha: 0, solid)
 *     shadow: "#000000" (alpha: 0.5)
 *
 * The role annotation is dropped when it just repeats the handle (`text`, `highlight`, `shadow`), so
 * the common singleton colours stay to a bare `handle: value` and the tokens go to the boxes that
 * actually need disambiguating.
 *
 * `changedTextColorDefault`, when present, is the scene design's default for the base text colour; it
 * rides on the `text` entry as the same `(changed from design default …)` marker other properties
 * get — the legend replaced the flat `colors` line the marker used to land on.
 */
export function formatColorLegend(
    colors: TextColorDescription[],
    prefix: string,
    changedTextColorDefault?: unknown,
): string {
    let output = `${prefix}  colors:\n`;
    for (const { handle, role, value, alpha, fillType } of colors) {
        const annotations = [
            ...(role !== handle ? [role] : []),
            ...(alpha !== undefined ? [`alpha: ${alpha}`] : []),
            ...(fillType !== undefined ? [fillType] : []),
        ];
        const suffix = annotations.length ? ` (${annotations.join(', ')})` : '';
        const marker =
            handle === 'text' && changedTextColorDefault !== undefined
                ? ` (changed from design default ${formatPropertyValue(changedTextColorDefault)})`
                : '';
        output += `${prefix}    ${handle}: "${value}"${suffix}${marker}\n`;
    }
    return output;
}

/**
 * Format an asset in YAML-style format for diffs and documentation.
 * This is the canonical format used throughout the codebase.
 *
 * @param asset The asset to format
 * @param prefix The line prefix (e.g., '+ ', '- ', or '' for plain format)
 * @returns Formatted asset string with all properties
 *
 * @example
 * ```typescript
 * // For git diff additions:
 * formatAsset(asset, '+ ')
 * // Output:
 * // + - type: MEDIA
 * // +   name: media_2
 * // +   bounds: "0 0 1920 1080"
 * // +   mediaId: "12345"
 *
 * // For schema documentation:
 * formatAsset(exampleAsset, '')
 * // Output:
 * // - type: MEDIA
 * //   name: <asset_name>
 * //   bounds: <x y width height>
 * ```
 */
export function formatAsset(asset: AssetDescription, prefix: string = ''): string {
    let output = '';

    // Header: type and name (following YAML list syntax)
    output += `${prefix}- type: ${asset.type}\n`;
    output += `${prefix}  name: ${asset.name}\n`;

    // Special handling for subscene (appears before other properties)
    if ('subscene' in asset && asset.subscene !== undefined) {
        output += `${prefix}  subscene: ${asset.subscene}\n`;
    }

    // Design defaults ride along as a sibling map rather than being folded into the values, so
    // each property keeps its own type (a fontSize stays a number, not a string that would render
    // quoted) and the marker is appended to the rendered line instead.
    const changedFromDesign = (
        CHANGED_FROM_DESIGN_KEY in asset ? asset.changedFromDesign : undefined
    ) as Record<string, unknown> | undefined;

    // All other properties (excluding name, type, subscene)
    for (const [propKey, propValue] of Object.entries(asset)) {
        if (propKey === 'name' || propKey === 'type' || propKey === 'subscene') {
            continue;
        }
        if (propKey === CHANGED_FROM_DESIGN_KEY) {
            continue;
        }
        if (propKey === 'colors' && isColorLegend(propValue)) {
            output += formatColorLegend(propValue, prefix, changedFromDesign?.colors);
            continue;
        }
        const designDefault = changedFromDesign?.[propKey];
        const marker =
            designDefault === undefined
                ? ''
                : ` (changed from design default ${formatPropertyValue(designDefault)})`;
        output += `${prefix}  ${propKey}: ${formatPropertyValue(propValue)}${marker}\n`;
    }

    return output;
}

/**
 * Format all properties of an object as key-value pairs.
 * Used for formatting video, template, music, and scene metadata.
 *
 * @param obj The object to format
 * @param prefix Line prefix (e.g., '+ ', '- ', or '' for plain format)
 * @param excludeKeys Keys to exclude from output
 * @returns Formatted string with one property per line
 *
 * @example
 * ```typescript
 * const video = { resolution: '1920x1080', aspectRatio: '16:9', duration: '5000ms' };
 * formatProperties(video, '+ ');
 * // Output:
 * // + resolution: 1920x1080
 * // + aspectRatio: 16:9
 * // + duration: 5000ms
 * ```
 */
export function formatProperties(
    obj: Record<string, unknown>,
    prefix: string = '',
    excludeKeys: string[] = [],
): string {
    let output = '';
    for (const [key, value] of Object.entries(obj)) {
        if (excludeKeys.includes(key)) {
            continue;
        }
        // null and undefined are spelled out rather than blanked, so a key that is present
        // with no value stays distinguishable from one holding an empty string.
        const formattedValue = value == null ? String(value) : stringifyUnknown(value);
        output += `${prefix}${key}: ${formattedValue}\n`;
    }
    return output;
}
